import { datadogRum } from '@datadog/browser-rum';
import { defer, type Deferred } from './defer';
import { runGuarded } from './runGuarded';
import { recordWAFTelemetry } from './telemetry';
import { isCrossTabRecoveryGateOpen, readClearedHintFresh } from './crossTab';
import {
  WAFClearanceDetail,
  WAFClearanceResolveState,
  WAFTelemetryOutcomeExtra,
  WAFTelemetryPhase,
  type WAFChallengeProvider,
  type WAFClearanceResult,
  type WAFClearanceStage,
  type WAFProvider,
} from './types';

/**
 * The replay budget granted to a solve→replay→re-challenge loop (clearance minted but the edge still
 * challenges) so it cannot spin forever. Each recovery call gets its own loop-local counter in
 * `recoverWAFChallenge`; both transports run that same shared loop. A cross-tab sibling clear grants the
 * budget once more per recovery call, so with the cross-tab flag on a loop may make up to two grants' worth
 * of attempts before exhausting.
 */
export const MAX_WAF_REPLAYS = 3;

// Outlasts Turnstile's own hung-widget detection (~41s), so a widget retrying on its own gets that chance before
// the user is told. A clearance never times out: the slow notice offers a reload instead, and Turnstile keeps
// retrying and refreshing itself behind it.
export const WAF_SLOW_CLEARANCE_DELAY_MS = 60_000;

interface ActiveClearance {
  deferred: Deferred<WAFClearanceResult>;
  challengeProvider: WAFChallengeProvider | undefined;
  startTimeMs: number;
  cancelSlowTimer: () => void;
  settled: boolean;
  isInteractive: boolean;
  isSlow: boolean;
  // Sticky: once true, stay true through settle so telemetry answers "was this flow ever interactive / slow?".
  wasEverInteractive: boolean;
  wasEverSlow: boolean;
}

class WAFChallengeManager {
  private provider: WAFChallengeProvider | undefined;
  private active: ActiveClearance | undefined;
  private readonly slowListeners = new Set<() => void>();
  // Set once a sibling clear has preempted an ever-interactive clearance; blocks a second such preemption until
  // a replay gets past the WAF (notifyClearanceValidated), so a sibling stuck re-challenging cannot cancel and
  // re-mount the user's turnstile repeatedly.
  private hasPreemptedInteractiveClearance = false;

  setChallengeProvider(provider: WAFChallengeProvider): void {
    this.provider = provider;
  }

  /**
   * CAUTION: between the `this.active` check and assigning `this.active` there must be NO `await` — a
   * microtask gap there would let two callers each build their own deferred, and only one could ever
   * settle. Everything up to `return` runs synchronously; the provider is invoked but not awaited here.
   */
  requestClearance(): Promise<WAFClearanceResult> {
    if (this.active) {
      return this.active.deferred.promise;
    }

    const active: ActiveClearance = {
      deferred: defer<WAFClearanceResult>(),
      challengeProvider: this.provider,
      startTimeMs: performance.now(),
      cancelSlowTimer: () => {},
      settled: false,
      isInteractive: false,
      isSlow: false,
      wasEverInteractive: false,
      wasEverSlow: false,
    };
    this.armSlowTimer(active);
    this.active = active;

    const provider = active.challengeProvider;
    if (!provider) {
      this.settle(active, WAFClearanceResolveState.Unavailable, WAFClearanceDetail.ProviderNotRegistered);
      return active.deferred.promise;
    }

    if (isCrossTabRecoveryGateOpen() && readClearedHintFresh()) {
      this.settle(active, WAFClearanceResolveState.Solved, WAFClearanceDetail.ClearedBySibling);
      return active.deferred.promise;
    }
    this.invokeProvider(active, provider);
    return active.deferred.promise;
  }

  private invokeProvider(active: ActiveClearance, provider: WAFChallengeProvider): void {
    try {
      provider
        .requestClearance()
        .then((result) => this.settle(active, result.state, result.detail, result.clearanceCookieIssued))
        .catch(() => this.settle(active, WAFClearanceResolveState.Error, WAFClearanceDetail.ProviderUnexpectedError));
    } catch {
      this.settle(active, WAFClearanceResolveState.Error, WAFClearanceDetail.ProviderUnexpectedError);
    }
  }

  waitForActiveClearance(): Promise<WAFClearanceResult> | undefined {
    return this.active?.deferred.promise;
  }

  /** False means the next requestClearance() caller creates the shared clearance rather than joining one. */
  hasActiveClearance(): boolean {
    return this.active !== undefined;
  }

  /** True while the in-flight clearance has been solving invisibly for WAF_SLOW_CLEARANCE_DELAY_MS without settling. */
  isActiveClearanceSlow = (): boolean => this.active?.isSlow === true;

  subscribeSlowClearance = (listener: () => void): (() => void) => {
    this.slowListeners.add(listener);
    return () => {
      this.slowListeners.delete(listener);
    };
  };

  private armSlowTimer(active: ActiveClearance): void {
    active.cancelSlowTimer();
    const timeoutId = setTimeout(() => this.markSlow(active), WAF_SLOW_CLEARANCE_DELAY_MS);
    active.cancelSlowTimer = () => clearTimeout(timeoutId);
  }

  private markSlow(active: ActiveClearance): void {
    if (active.settled) {
      return;
    }
    active.isSlow = true;
    active.wasEverSlow = true;
    this.notifySlowListeners();
    this.recordSlowTelemetry(active);
  }

  private clearSlow(active: ActiveClearance): void {
    if (!active.isSlow) {
      return;
    }
    active.isSlow = false;
    this.notifySlowListeners();
  }

  private notifySlowListeners(): void {
    this.slowListeners.forEach((listener) => runGuarded(listener));
  }

  /**
   * A human is now solving interactively: the interactive overlay replaces any slow notice, and the slow timer
   * stops, since an interactive challenge waits on the user rather than on the network. Does NOT settle.
   */
  notifyInteractiveStarted(): void {
    const active = this.active;
    if (!active || active.settled) {
      return;
    }
    active.wasEverInteractive = true;
    active.isInteractive = true;
    active.cancelSlowTimer();
    this.clearSlow(active);
  }

  /**
   * The challenge left interactive mode (the user finished it, or Turnstile went back to invisible processing).
   * No-op for a flow that is not interactive, so a stray exit edge cannot restart a running slow timer.
   */
  notifyInteractiveEnded(): void {
    const active = this.active;
    if (!active || active.settled || !active.isInteractive) {
      return;
    }
    active.isInteractive = false;
    this.armSlowTimer(active);
  }

  notifyScriptLoaded(wasScriptAlreadyLoaded: boolean): void {
    const active = this.active;
    if (!active || active.settled) {
      return;
    }
    const {
      durationMs: scriptLoadDurationMs,
      pageVisibilityState,
      wafProvider,
    } = this.readClearanceTelemetryContext(active);
    const sequenceId = recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: WAFTelemetryOutcomeExtra.ScriptLoaded,
      wafProvider,
      pageVisibilityState,
      scriptLoadDurationMs,
      wasScriptAlreadyLoaded,
    });
    runGuarded(() => {
      // An already-loaded script measures no download, so it stays out of the vital's percentiles.
      if (scriptLoadDurationMs === undefined || wasScriptAlreadyLoaded) {
        return;
      }
      datadogRum.addDurationVital('waf_challenge_script_load', {
        startTime: Date.now() - scriptLoadDurationMs,
        duration: scriptLoadDurationMs,
        context: {
          sequenceId,
          ...(wafProvider !== undefined ? { wafProvider } : {}),
          ...(pageVisibilityState !== undefined ? { pageVisibilityState } : {}),
        },
      });
    });
  }

  /**
   * A sibling tab solved and minted the shared cookie, so settle the in-flight clearance Solved without
   * solving ourselves — settle() tears the provider's widget down and releases parked requests to replay on
   * the shared cookie. Flag re-checked at call time so a flag-off tab ignores a flag-on sibling. Idempotent
   * no-op when nothing is in flight or it already settled.
   *
   * An ever-interactive clearance is preempted at most once until a replay validates a clearance
   * ({@link notifyClearanceValidated}): the first sibling clear optimistically closes the modal, but a further
   * one is refused until a replay confirms the clear actually worked — so a sibling stuck re-challenging (whose
   * replays keep failing, never validating) can't cancel and re-mount the user's turnstile repeatedly.
   * Invisible clearances are always settled.
   */
  settleClearedBySibling(): void {
    if (!isCrossTabRecoveryGateOpen()) {
      return;
    }
    const active = this.active;
    if (!active || active.settled) {
      return;
    }
    if (active.wasEverInteractive) {
      if (this.hasPreemptedInteractiveClearance) {
        return;
      }
      this.hasPreemptedInteractiveClearance = true;
    }
    this.settle(active, WAFClearanceResolveState.Solved, WAFClearanceDetail.ClearedBySibling);
  }

  /**
   * A replay came back (or failed at the origin/network) rather than being re-challenged, so re-arm one cross-tab
   * interactive preemption. Only a re-challenge — a wrong sibling clear — withholds the re-arm, leaving its guard
   * set so the user is not preempted again. The recovery loop calls this for a returned replay and for a thrown
   * one (a throw is not proof the edge cleared, but a wrong re-arm here is cheap and self-correcting).
   */
  notifyClearanceValidated(): void {
    this.hasPreemptedInteractiveClearance = false;
  }

  /**
   * Idempotent and identified by the `active` its callbacks captured, so a provider result and a sibling clear
   * race but only the first settle acts. The flow is released (this.active cleared, the deferred resolved)
   * BEFORE the best-effort notify/teardown/telemetry, so nothing there can strand it — a stranded flow would hang
   * every later request for the tab's lifetime.
   */
  private settle(
    active: ActiveClearance,
    state: WAFClearanceResolveState,
    detail?: WAFClearanceDetail,
    clearanceCookieIssued?: boolean,
  ): void {
    if (active.settled) {
      return;
    }
    active.settled = true;
    active.cancelSlowTimer();
    if (this.active === active) {
      this.active = undefined;
    }
    active.deferred.resolve(detail !== undefined ? { state, detail } : { state });
    this.clearSlow(active);
    runGuarded(() => active.challengeProvider?.cancelClearance());
    this.recordSettleTelemetry(active, state, detail, clearanceCookieIssued);
  }

  private readClearanceTelemetryContext(active: ActiveClearance): {
    durationMs: number | undefined;
    pageVisibilityState: DocumentVisibilityState | undefined;
    wafProvider: WAFProvider | undefined;
  } {
    let durationMs: number | undefined;
    let pageVisibilityState: DocumentVisibilityState | undefined;
    let wafProvider: WAFProvider | undefined;
    runGuarded(() => {
      // A non-finite or failed timing read leaves durationMs undefined, so the vital is omitted rather
      // than publishing a fabricated or NaN duration that skews the latency percentiles. The property
      // read comes last so a throwing foreign getter cannot take the duration and visibility reads down.
      const elapsedMs = Math.round(performance.now() - active.startTimeMs);
      durationMs = Number.isFinite(elapsedMs) ? elapsedMs : undefined;
      pageVisibilityState = document.visibilityState;
      wafProvider = active.challengeProvider?.provider;
    });
    return { durationMs, pageVisibilityState, wafProvider };
  }

  private recordSlowTelemetry(active: ActiveClearance): void {
    const { pageVisibilityState, wafProvider } = this.readClearanceTelemetryContext(active);
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: WAFTelemetryOutcomeExtra.Slow,
      wafProvider,
      pageVisibilityState,
      wasEverInteractive: active.wasEverInteractive,
      clearanceStage: this.readStage(active),
    });
  }

  private recordSettleTelemetry(
    active: ActiveClearance,
    state: WAFClearanceResolveState,
    detail: WAFClearanceDetail | undefined,
    clearanceCookieIssued: boolean | undefined,
  ): void {
    const { durationMs, pageVisibilityState, wafProvider } = this.readClearanceTelemetryContext(active);
    const sequenceId = recordWAFTelemetry({
      phase: WAFTelemetryPhase.Clearance,
      outcome: state,
      wafProvider,
      durationMs,
      pageVisibilityState,
      wasEverInteractive: active.wasEverInteractive,
      wasEverSlow: active.wasEverSlow,
      detail,
      clearanceCookieIssued,
    });
    runGuarded(() => {
      if (durationMs === undefined) {
        return;
      }
      datadogRum.addDurationVital('waf_challenge_clearance', {
        startTime: Date.now() - durationMs,
        duration: durationMs,
        context: {
          state,
          wasEverInteractive: active.wasEverInteractive,
          wasEverSlow: active.wasEverSlow,
          sequenceId,
          ...(wafProvider !== undefined ? { wafProvider } : {}),
          ...(pageVisibilityState !== undefined ? { pageVisibilityState } : {}),
          ...(detail !== undefined ? { detail } : {}),
          ...(clearanceCookieIssued !== undefined ? { clearanceCookieIssued } : {}),
        },
      });
    });
  }

  private readStage(active: ActiveClearance): WAFClearanceStage | undefined {
    try {
      return active.challengeProvider?.describeStage?.();
    } catch {
      return undefined;
    }
  }

  TestOnlyReset(): void {
    if (this.active) {
      this.active.settled = true;
      this.active.cancelSlowTimer();
      this.active.deferred.resolve({ state: WAFClearanceResolveState.Unavailable });
      this.active = undefined;
    }
    this.slowListeners.clear();
    this.provider = undefined;
    this.hasPreemptedInteractiveClearance = false;
  }
}

export const wafChallengeManager = new WAFChallengeManager();

export function TestOnlyResetWAFChallengeManager(): void {
  wafChallengeManager.TestOnlyReset();
}
